/**
 * Editable MarketEntryConfig workbench.
 *
 * Faithful port of Kaiyang's `VehicleConfigurator`
 * (RegPulse-Kaiyang-Contest/.../components/readiness-board/
 * VehicleConfigurator.tsx). Renders 16 numbered fieldsets covering every
 * leaf of the vendored MarketEntryConfig interface; nullable fields use
 * a tri-state select (Unknown / yes / no) so `null` remains visible
 * (Kaiyang Lesson §25.4). Re-evaluation is manual via a Run button.
 *
 * Contract w/ App.tsx:
 *   - props.draft        — current edit buffer (controlled)
 *   - props.appliedHash  — hash of the last successfully evaluated config
 *   - props.onChange     — fires on every patch (App owns state)
 *   - props.onRun        — fires when the user clicks "Run applicability"
 *   - props.onLoadSample — fires when the user clicks a sample button
 *   - props.samples      — sample templates from /v1/seed-config
 *   - props.loading      — evaluator currently in flight
 *
 * No live Zod parse — matches Kaiyang behaviour. Submit-time validation
 * happens server-side; failures surface in App's error banner.
 */

import { useMemo } from "react";

// ─── Shape of the config we edit ──────────────────────────────────────────────
// We keep an internal mutable shape that mirrors MarketEntryConfig but lets
// every nested struct be `null` so the editor can render "Unknown" first.

type Powertrain = "BEV" | "PHEV" | "HEV" | "ICE" | "FCEV" | null;
type AutomationLevel = "L0" | "L1" | "L2" | "L2+" | "L3" | null;
type EuMarket = "DE" | "FR" | "NL" | "UK" | "ES" | "IT" | "PL" | "SE";
type VehicleCategory = "M1" | "M2" | "M3" | "N1" | "N2" | "N3";
type ApprovalType = "new_type" | "extension" | "individual";
type SteeringPosition = "LHD" | "RHD";
type CompletionState = "complete" | "incomplete" | "completed";
type ConsumerOrFleet = "consumer" | "fleet";
type SalesModel = "dealer" | "direct" | "subscription";
type ChargingOperatorRole = "oem" | "cpo" | "msp" | "none";
type CommercialRole = "manufacturer" | "importer" | "distributor" | "dealer";
type SteeringAssistType = "EPS" | "HPS" | "EHPS";
type UkMarketRoute = "GB" | "NI" | "GB+NI";
type ApprovalAuthority = "KBA" | "UTAC" | "RDW" | "IDIADA" | "VCA" | "other";
type LaneChange = "C1" | "C2" | "C3";
type AiLevel = "none" | "narrow" | "foundation_model";
type AiDataFlag = "cabin_camera" | "driver_profiling" | "emotion_recognition";

export interface EditableConfig {
  projectName: string | null;
  vehicleCategory: VehicleCategory | null;
  steeringAssistType: SteeringAssistType | null;
  isSampleTemplate: boolean;
  targetMarkets: { eu_baseline: boolean; selected_member_states: EuMarket[] };
  sopDate: string | null;
  typeApprovalApplicationDate: string | null;
  firstRegistrationDate: string | null;
  powertrain: Powertrain;
  tractionBattery: { capacityBand: "small" | "medium" | "large" } | null;
  batteryTechnical: { exactCapacityKWh: number | null; chemistry: string | null } | null;
  automation: { levelMax: AutomationLevel; fitsSilc: boolean; laneChange: LaneChange[] } | null;
  adasFeatureInventory: {
    isa: boolean | null;
    aeb: boolean | null;
    elks: boolean | null;
    addw: boolean | null;
  } | null;
  dms: { fitted: boolean; biometricCategorisation: boolean; poseOnly: boolean } | null;
  aiSystem: {
    present: boolean;
    level: AiLevel;
    safetyComponent: boolean;
    dataFlags: AiDataFlag[];
  } | null;
  ota: { enabled: boolean | null; functionalExtension: boolean } | null;
  softwareUpdate: { rxSwinMapAvailable: boolean | null } | null;
  cybersecurity: { relevantR155: boolean } | null;
  connectivity: { personalDataProcessing: boolean; dataSharingThirdParty: boolean } | null;
  charging: { ac: boolean; dc: boolean; bidirectional: boolean; homeChargingFr8A: boolean } | null;
  hvSafety: { r100EvidenceAvailable: boolean | null } | null;
  reachesAftermarket: boolean;
  approvalType: ApprovalType | null;
  steeringPosition: SteeringPosition | null;
  completionState: CompletionState | null;
  consumerOrFleet: ConsumerOrFleet | null;
  salesModel: SalesModel | null;
  parkingAutomation: boolean | null;
  chargingOperatorRole: ChargingOperatorRole | null;
  commercialRole: CommercialRole | null;
  ukMarketRoute: UkMarketRoute | null;
  approvalRoute: { approvalAuthority: ApprovalAuthority | null; technicalService: string | null } | null;
}

const EU_MARKETS: EuMarket[] = ["DE", "FR", "NL", "UK", "ES", "IT", "PL", "SE"];
const VEHICLE_CATEGORIES: VehicleCategory[] = ["M1", "M2", "M3", "N1", "N2", "N3"];
const POWERTRAINS: Exclude<Powertrain, null>[] = ["BEV", "PHEV", "HEV", "ICE", "FCEV"];
const AUTOMATION_LEVELS: Exclude<AutomationLevel, null>[] = ["L0", "L1", "L2", "L2+", "L3"];
const LANE_CHANGE_TYPES: LaneChange[] = ["C1", "C2", "C3"];
const STEERING_ASSIST: SteeringAssistType[] = ["EPS", "HPS", "EHPS"];
const STEERING_POSITIONS: SteeringPosition[] = ["LHD", "RHD"];
const APPROVAL_TYPES: ApprovalType[] = ["new_type", "extension", "individual"];
const COMPLETION_STATES: CompletionState[] = ["complete", "incomplete", "completed"];
const CONSUMER_OR_FLEET: ConsumerOrFleet[] = ["consumer", "fleet"];
const SALES_MODELS: SalesModel[] = ["dealer", "direct", "subscription"];
const CHARGING_ROLES: ChargingOperatorRole[] = ["oem", "cpo", "msp", "none"];
const COMMERCIAL_ROLES: CommercialRole[] = ["manufacturer", "importer", "distributor", "dealer"];
const UK_ROUTES: UkMarketRoute[] = ["GB", "NI", "GB+NI"];
const APPROVAL_AUTHORITIES: ApprovalAuthority[] = ["KBA", "UTAC", "RDW", "IDIADA", "VCA", "other"];
const CAPACITY_BANDS: Array<"small" | "medium" | "large"> = ["small", "medium", "large"];
const AI_LEVELS: AiLevel[] = ["none", "narrow", "foundation_model"];
const AI_DATA_FLAGS: AiDataFlag[] = ["cabin_camera", "driver_profiling", "emotion_recognition"];

// ─── Hash helper for dirty-tracking (cheap, stable, ASCII-safe) ──────────────
export function hashConfig(c: unknown): string {
  return JSON.stringify(c);
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface Sample {
  id: string;
  label: string;
  config: EditableConfig;
}

interface Props {
  draft: EditableConfig;
  appliedHash: string | null;
  onChange: (next: EditableConfig) => void;
  onRun: () => void;
  onReset: () => void;
  onLoadSample: (sampleId: string) => void;
  samples: Sample[];
  loading: boolean;
}

export function ConfigEditor({
  draft,
  appliedHash,
  onChange,
  onRun,
  onReset,
  onLoadSample,
  samples,
  loading,
}: Props) {
  const draftHash = useMemo(() => hashConfig(draft), [draft]);
  const dirty = appliedHash !== null && appliedHash !== draftHash;
  const neverRun = appliedHash === null;

  // Kaiyang gate: at least one market (EU baseline or named member state).
  const minimumFieldsFilled =
    draft.targetMarkets.eu_baseline || draft.targetMarkets.selected_member_states.length > 0;

  const patch = <K extends keyof EditableConfig>(key: K, value: EditableConfig[K]) => {
    onChange({ ...draft, [key]: value });
  };

  return (
    <section className="panel" aria-labelledby="config-editor-title" data-testid="config-editor">
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "var(--space-8)" }}>
        <h2 id="config-editor-title" className="panel__title">Configuration</h2>
        <span
          className="type-mono"
          data-testid="dirty-state"
          style={{
            fontSize: 10,
            padding: "1px var(--space-8)",
            borderRadius: "var(--radius-full)",
            border: "1px solid var(--color-border-strong)",
            background: neverRun
              ? "var(--status-candidate-pending-soft)"
              : dirty
                ? "var(--status-unknown-config-soft)"
                : "var(--status-canonical-ok-soft)",
          }}
        >
          {neverRun ? "not yet run" : dirty ? "edits pending · re-run" : "in sync"}
        </span>
      </header>
      <p className="panel__caption">
        Edit any field and press <strong>Run applicability</strong>. All 46+ leaves of the
        Kaiyang MarketEntryConfig contract are exposed below.
      </p>

      {/* Sample templates */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Sample templates</legend>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-8)" }}>
          {samples.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onLoadSample(s.id)}
              style={chipButtonStyle}
              data-testid={`sample-${s.id}`}
            >
              Load · {s.label}
            </button>
          ))}
          <button type="button" onClick={onReset} style={ghostButtonStyle} data-testid="reset-draft">
            ↺ Reset draft
          </button>
        </div>
      </fieldset>

      <Section index={1} title="Vehicle identity">
        <TextField
          label="Project name"
          value={draft.projectName ?? ""}
          placeholder="e.g. MY2027 BEV Pilot"
          onChange={(v) => patch("projectName", v || null)}
        />
        <Row>
          <CheckboxField
            label="Sample template"
            checked={draft.isSampleTemplate}
            onChange={(b) => patch("isSampleTemplate", b)}
          />
          <SelectField<VehicleCategory>
            label="Vehicle category"
            value={draft.vehicleCategory}
            options={VEHICLE_CATEGORIES}
            onChange={(v) => patch("vehicleCategory", v)}
          />
        </Row>
        <Row>
          <SelectField<SteeringAssistType>
            label="Steering assist"
            value={draft.steeringAssistType}
            options={STEERING_ASSIST}
            onChange={(v) => patch("steeringAssistType", v)}
          />
          <SelectField<SteeringPosition>
            label="Steering position"
            value={draft.steeringPosition}
            options={STEERING_POSITIONS}
            onChange={(v) => patch("steeringPosition", v)}
          />
        </Row>
      </Section>

      <Section index={2} title="Target markets">
        <CheckboxField
          label="EU baseline (applies to all EU member states)"
          checked={draft.targetMarkets.eu_baseline}
          onChange={(b) =>
            patch("targetMarkets", { ...draft.targetMarkets, eu_baseline: b })
          }
        />
        <ChipMultiSelect
          label="Selected member states"
          values={draft.targetMarkets.selected_member_states}
          options={EU_MARKETS}
          onToggle={(opt) => {
            const set = new Set(draft.targetMarkets.selected_member_states);
            if (set.has(opt)) set.delete(opt);
            else set.add(opt);
            patch("targetMarkets", {
              ...draft.targetMarkets,
              selected_member_states: EU_MARKETS.filter((m) => set.has(m)),
            });
          }}
        />
      </Section>

      <Section index={3} title="Timing">
        <Row>
          <DateField
            label="Type approval application"
            value={draft.typeApprovalApplicationDate}
            onChange={(v) => patch("typeApprovalApplicationDate", v)}
          />
          <DateField
            label="Start of production (SOP)"
            value={draft.sopDate}
            onChange={(v) => patch("sopDate", v)}
          />
          <DateField
            label="First registration"
            value={draft.firstRegistrationDate}
            onChange={(v) => patch("firstRegistrationDate", v)}
          />
        </Row>
      </Section>

      <Section index={4} title="Powertrain & emissions">
        <Row>
          <SelectField<Exclude<Powertrain, null>>
            label="Powertrain"
            value={draft.powertrain}
            options={POWERTRAINS}
            onChange={(v) => patch("powertrain", v)}
          />
        </Row>
      </Section>

      <Section index={5} title="Battery & materials">
        <Row>
          <SelectField<"small" | "medium" | "large">
            label="Traction battery capacity band"
            value={draft.tractionBattery?.capacityBand ?? null}
            options={CAPACITY_BANDS}
            onChange={(v) => patch("tractionBattery", v ? { capacityBand: v } : null)}
          />
          <NumberField
            label="Exact capacity (kWh)"
            value={draft.batteryTechnical?.exactCapacityKWh ?? null}
            min={1}
            step={0.1}
            onChange={(n) =>
              patch("batteryTechnical", {
                exactCapacityKWh: n,
                chemistry: draft.batteryTechnical?.chemistry ?? null,
              })
            }
          />
          <TextField
            label="Cell chemistry"
            value={draft.batteryTechnical?.chemistry ?? ""}
            placeholder="e.g. NMC, LFP"
            onChange={(v) =>
              patch("batteryTechnical", {
                exactCapacityKWh: draft.batteryTechnical?.exactCapacityKWh ?? null,
                chemistry: v || null,
              })
            }
          />
        </Row>
        <TriStateField
          label="R100 evidence available (HV safety)"
          value={draft.hvSafety?.r100EvidenceAvailable ?? null}
          onChange={(v) => patch("hvSafety", { r100EvidenceAvailable: v })}
        />
      </Section>

      <Section index={6} title="ADAS & automation">
        <Row>
          <SelectField<Exclude<AutomationLevel, null>>
            label="Max automation level"
            value={draft.automation?.levelMax ?? null}
            options={AUTOMATION_LEVELS}
            onChange={(v) =>
              patch("automation", {
                levelMax: v,
                fitsSilc: draft.automation?.fitsSilc ?? false,
                laneChange: draft.automation?.laneChange ?? [],
              })
            }
          />
          <CheckboxField
            label="Fits SiLC scope"
            checked={draft.automation?.fitsSilc ?? false}
            onChange={(b) =>
              patch("automation", {
                levelMax: draft.automation?.levelMax ?? null,
                fitsSilc: b,
                laneChange: draft.automation?.laneChange ?? [],
              })
            }
          />
        </Row>
        <ChipMultiSelect
          label="Lane-change feature types"
          values={draft.automation?.laneChange ?? []}
          options={LANE_CHANGE_TYPES}
          onToggle={(opt) => {
            const set = new Set(draft.automation?.laneChange ?? []);
            if (set.has(opt)) set.delete(opt);
            else set.add(opt);
            patch("automation", {
              levelMax: draft.automation?.levelMax ?? null,
              fitsSilc: draft.automation?.fitsSilc ?? false,
              laneChange: LANE_CHANGE_TYPES.filter((t) => set.has(t)),
            });
          }}
        />
        <Row>
          <TriStateField
            label="ISA fitted"
            value={draft.adasFeatureInventory?.isa ?? null}
            onChange={(v) => patchAdas(draft, patch, "isa", v)}
          />
          <TriStateField
            label="AEB fitted"
            value={draft.adasFeatureInventory?.aeb ?? null}
            onChange={(v) => patchAdas(draft, patch, "aeb", v)}
          />
          <TriStateField
            label="ELKS fitted"
            value={draft.adasFeatureInventory?.elks ?? null}
            onChange={(v) => patchAdas(draft, patch, "elks", v)}
          />
          <TriStateField
            label="ADDW fitted"
            value={draft.adasFeatureInventory?.addw ?? null}
            onChange={(v) => patchAdas(draft, patch, "addw", v)}
          />
        </Row>
        <TriStateField
          label="Parking automation"
          value={draft.parkingAutomation ?? null}
          onChange={(v) => patch("parkingAutomation", v)}
        />
      </Section>

      <Section index={7} title="HMI & in-cabin (DMS)">
        <Row>
          <CheckboxField
            label="DMS fitted"
            checked={draft.dms?.fitted ?? false}
            onChange={(b) =>
              patch("dms", {
                fitted: b,
                biometricCategorisation: draft.dms?.biometricCategorisation ?? false,
                poseOnly: draft.dms?.poseOnly ?? false,
              })
            }
          />
          <CheckboxField
            label="Biometric categorisation"
            checked={draft.dms?.biometricCategorisation ?? false}
            onChange={(b) =>
              patch("dms", {
                fitted: draft.dms?.fitted ?? false,
                biometricCategorisation: b,
                poseOnly: draft.dms?.poseOnly ?? false,
              })
            }
          />
          <CheckboxField
            label="Pose-only (no biometric)"
            checked={draft.dms?.poseOnly ?? false}
            onChange={(b) =>
              patch("dms", {
                fitted: draft.dms?.fitted ?? false,
                biometricCategorisation: draft.dms?.biometricCategorisation ?? false,
                poseOnly: b,
              })
            }
          />
        </Row>
      </Section>

      <Section index={8} title="OTA & SUMS">
        <Row>
          <TriStateField
            label="OTA enabled"
            value={draft.ota?.enabled ?? null}
            onChange={(v) =>
              patch("ota", { enabled: v, functionalExtension: draft.ota?.functionalExtension ?? false })
            }
          />
          <CheckboxField
            label="Functional extension OTAs"
            checked={draft.ota?.functionalExtension ?? false}
            onChange={(b) =>
              patch("ota", { enabled: draft.ota?.enabled ?? null, functionalExtension: b })
            }
          />
          <TriStateField
            label="RXSWIN map available"
            value={draft.softwareUpdate?.rxSwinMapAvailable ?? null}
            onChange={(v) => patch("softwareUpdate", { rxSwinMapAvailable: v })}
          />
        </Row>
      </Section>

      <Section index={9} title="Cybersecurity">
        <CheckboxField
          label="R155 relevant"
          checked={draft.cybersecurity?.relevantR155 ?? false}
          onChange={(b) => patch("cybersecurity", { relevantR155: b })}
        />
      </Section>

      <Section index={10} title="Connectivity & data">
        <Row>
          <CheckboxField
            label="Processes personal data"
            checked={draft.connectivity?.personalDataProcessing ?? false}
            onChange={(b) =>
              patch("connectivity", {
                personalDataProcessing: b,
                dataSharingThirdParty: draft.connectivity?.dataSharingThirdParty ?? false,
              })
            }
          />
          <CheckboxField
            label="Shares data with third parties"
            checked={draft.connectivity?.dataSharingThirdParty ?? false}
            onChange={(b) =>
              patch("connectivity", {
                personalDataProcessing: draft.connectivity?.personalDataProcessing ?? false,
                dataSharingThirdParty: b,
              })
            }
          />
        </Row>
      </Section>

      <Section index={11} title="Charging & energy">
        <Row>
          <CheckboxField
            label="AC charging"
            checked={draft.charging?.ac ?? false}
            onChange={(b) => patchCharging(draft, patch, "ac", b)}
          />
          <CheckboxField
            label="DC charging"
            checked={draft.charging?.dc ?? false}
            onChange={(b) => patchCharging(draft, patch, "dc", b)}
          />
          <CheckboxField
            label="Bidirectional (V2G/V2L)"
            checked={draft.charging?.bidirectional ?? false}
            onChange={(b) => patchCharging(draft, patch, "bidirectional", b)}
          />
          <CheckboxField
            label="FR §8A home charging"
            checked={draft.charging?.homeChargingFr8A ?? false}
            onChange={(b) => patchCharging(draft, patch, "homeChargingFr8A", b)}
          />
        </Row>
        <SelectField<ChargingOperatorRole>
          label="Charging operator role"
          value={draft.chargingOperatorRole}
          options={CHARGING_ROLES}
          onChange={(v) => patch("chargingOperatorRole", v)}
        />
      </Section>

      <Section index={12} title="Aftersales & RMI scope">
        <CheckboxField
          label="Reaches aftermarket"
          checked={draft.reachesAftermarket}
          onChange={(b) => patch("reachesAftermarket", b)}
        />
      </Section>

      <Section index={13} title="Post-market surveillance (informational)">
        <p className="type-caption" style={captionStyle}>
          Post-market surveillance obligations are derived from the rules + applicability
          outcome; no extra config field today (W6 may add a vigilance contact).
        </p>
      </Section>

      <Section index={14} title="Member-state operation (informational)">
        <p className="type-caption" style={captionStyle}>
          Per-member-state overlays are driven from §2 (Target markets) + §16 (UK route).
        </p>
      </Section>

      <Section index={15} title="Sales economics & approval shape">
        <Row>
          <SelectField<ApprovalType>
            label="Approval type"
            value={draft.approvalType}
            options={APPROVAL_TYPES}
            onChange={(v) => patch("approvalType", v)}
          />
          <SelectField<CompletionState>
            label="Completion state"
            value={draft.completionState}
            options={COMPLETION_STATES}
            onChange={(v) => patch("completionState", v)}
          />
        </Row>
        <Row>
          <SelectField<ConsumerOrFleet>
            label="Consumer or fleet"
            value={draft.consumerOrFleet}
            options={CONSUMER_OR_FLEET}
            onChange={(v) => patch("consumerOrFleet", v)}
          />
          <SelectField<SalesModel>
            label="Sales model"
            value={draft.salesModel}
            options={SALES_MODELS}
            onChange={(v) => patch("salesModel", v)}
          />
          <SelectField<CommercialRole>
            label="Commercial role"
            value={draft.commercialRole}
            options={COMMERCIAL_ROLES}
            onChange={(v) => patch("commercialRole", v)}
          />
        </Row>
        <Row>
          <SelectField<ApprovalAuthority>
            label="Approval authority"
            value={draft.approvalRoute?.approvalAuthority ?? null}
            options={APPROVAL_AUTHORITIES}
            onChange={(v) =>
              patch("approvalRoute", {
                approvalAuthority: v,
                technicalService: draft.approvalRoute?.technicalService ?? null,
              })
            }
          />
          <TextField
            label="Technical service"
            value={draft.approvalRoute?.technicalService ?? ""}
            placeholder="e.g. TÜV SÜD"
            onChange={(v) =>
              patch("approvalRoute", {
                approvalAuthority: draft.approvalRoute?.approvalAuthority ?? null,
                technicalService: v || null,
              })
            }
          />
        </Row>
      </Section>

      <Section index={16} title="UK / GB / NI market route">
        {!draft.targetMarkets.selected_member_states.includes("UK") ? (
          <p className="type-caption" style={captionStyle}>
            UK is not in <em>Selected member states</em>. UK route stays disabled.
          </p>
        ) : (
          <SelectField<UkMarketRoute>
            label="UK route"
            value={draft.ukMarketRoute}
            options={UK_ROUTES}
            onChange={(v) => patch("ukMarketRoute", v)}
            help="Kaiyang Lesson §25.4 R7.1 — UK selected without an explicit route routes GB-only obligations to unknown_needs_config."
          />
        )}
      </Section>

      <Section index={17} title="AI & Data">
        <Row>
          <CheckboxField
            label="Vehicle uses an AI system"
            checked={draft.aiSystem?.present ?? false}
            onChange={(b) => patchAi(draft, patch, { present: b })}
          />
          <CheckboxField
            label="AI is a safety component"
            checked={draft.aiSystem?.safetyComponent ?? false}
            onChange={(b) => patchAi(draft, patch, { safetyComponent: b })}
          />
        </Row>
        <Row>
          <SelectField<AiLevel>
            label="AI system level"
            value={draft.aiSystem?.level ?? null}
            options={AI_LEVELS}
            onChange={(v) => patchAi(draft, patch, { level: v ?? "none" })}
          />
        </Row>
        <ChipMultiSelect
          label="AI data flags"
          values={draft.aiSystem?.dataFlags ?? []}
          options={AI_DATA_FLAGS}
          onToggle={(opt) => {
            const set = new Set(draft.aiSystem?.dataFlags ?? []);
            if (set.has(opt)) set.delete(opt);
            else set.add(opt);
            patchAi(draft, patch, {
              dataFlags: AI_DATA_FLAGS.filter((f) => set.has(f)),
            });
          }}
        />
      </Section>

      <div
        style={{
          position: "sticky",
          bottom: 0,
          background: "var(--color-surface-elevated)",
          padding: "var(--space-12) 0 0 0",
          marginTop: "var(--space-16)",
          borderTop: "1px solid var(--color-border)",
          display: "flex",
          gap: "var(--space-8)",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          onClick={onRun}
          disabled={loading || !minimumFieldsFilled}
          style={primaryButtonStyle(loading || !minimumFieldsFilled)}
          data-testid="run-applicability"
        >
          {loading ? "Evaluating…" : neverRun ? "▶ Run applicability" : dirty ? "▶ Re-run applicability" : "✓ In sync · re-run"}
        </button>
        {!minimumFieldsFilled && (
          <span className="type-caption" style={{ color: "var(--status-candidate-pending)" }}>
            Configure target markets to enable.
          </span>
        )}
      </div>
    </section>
  );
}

// ─── Internal helpers ────────────────────────────────────────────────────────

function patchAdas(
  draft: EditableConfig,
  patch: <K extends keyof EditableConfig>(k: K, v: EditableConfig[K]) => void,
  field: "isa" | "aeb" | "elks" | "addw",
  v: boolean | null,
) {
  patch("adasFeatureInventory", {
    isa: field === "isa" ? v : draft.adasFeatureInventory?.isa ?? null,
    aeb: field === "aeb" ? v : draft.adasFeatureInventory?.aeb ?? null,
    elks: field === "elks" ? v : draft.adasFeatureInventory?.elks ?? null,
    addw: field === "addw" ? v : draft.adasFeatureInventory?.addw ?? null,
  });
}

function patchAi(
  draft: EditableConfig,
  patch: <K extends keyof EditableConfig>(k: K, v: EditableConfig[K]) => void,
  partial: Partial<NonNullable<EditableConfig["aiSystem"]>>,
) {
  const current: NonNullable<EditableConfig["aiSystem"]> = draft.aiSystem ?? {
    present: false,
    level: "none",
    safetyComponent: false,
    dataFlags: [],
  };
  patch("aiSystem", { ...current, ...partial });
}

function patchCharging(
  draft: EditableConfig,
  patch: <K extends keyof EditableConfig>(k: K, v: EditableConfig[K]) => void,
  field: "ac" | "dc" | "bidirectional" | "homeChargingFr8A",
  b: boolean,
) {
  patch("charging", {
    ac: field === "ac" ? b : draft.charging?.ac ?? false,
    dc: field === "dc" ? b : draft.charging?.dc ?? false,
    bidirectional: field === "bidirectional" ? b : draft.charging?.bidirectional ?? false,
    homeChargingFr8A:
      field === "homeChargingFr8A" ? b : draft.charging?.homeChargingFr8A ?? false,
  });
}

// ─── Field widgets ───────────────────────────────────────────────────────────

const fieldsetStyle: React.CSSProperties = {
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-4)",
  padding: "var(--space-8) var(--space-12)",
  margin: "var(--space-8) 0",
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-8)",
};
const legendStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--color-text-secondary)",
  padding: "0 var(--space-4)",
};
const captionStyle: React.CSSProperties = { margin: 0, fontStyle: "italic" };
const chipButtonStyle: React.CSSProperties = {
  fontSize: 12,
  padding: "var(--space-4) var(--space-12)",
  background: "var(--color-surface-sunken)",
  border: "1px solid var(--color-border-strong)",
  borderRadius: "var(--radius-full)",
  cursor: "pointer",
  color: "var(--color-text-primary)",
};
const ghostButtonStyle: React.CSSProperties = {
  ...chipButtonStyle,
  background: "transparent",
};
function primaryButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    fontSize: 14,
    fontWeight: 600,
    padding: "var(--space-8) var(--space-16)",
    background: disabled ? "var(--color-surface-sunken)" : "var(--color-accent)",
    color: disabled ? "var(--color-text-secondary)" : "var(--color-text-on-accent)",
    border: "1px solid " + (disabled ? "var(--color-border-strong)" : "var(--color-accent)"),
    borderRadius: "var(--radius-4)",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.7 : 1,
  };
}

function Section({
  index,
  title,
  children,
}: {
  index: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset
      style={fieldsetStyle}
      data-testid={`section-${index}`}
    >
      <legend style={legendStyle}>
        {String(index).padStart(2, "0")} · {title}
      </legend>
      {children}
    </fieldset>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "var(--space-8)",
        alignItems: "flex-end",
      }}
    >
      {children}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-4)",
  flex: "1 1 160px",
  minWidth: 160,
  fontSize: 12,
};
const labelTextStyle: React.CSSProperties = {
  color: "var(--color-text-secondary)",
  fontWeight: 600,
};
const inputStyle: React.CSSProperties = {
  font: "inherit",
  fontSize: 13,
  padding: "var(--space-4) var(--space-8)",
  background: "var(--color-surface-elevated)",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-4)",
  color: "var(--color-text-primary)",
};

function TextField({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  return (
    <label style={labelStyle}>
      <span style={labelTextStyle}>{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      />
    </label>
  );
}

function NumberField({
  label,
  value,
  min,
  step,
  onChange,
}: {
  label: string;
  value: number | null;
  min?: number;
  step?: number;
  onChange: (v: number | null) => void;
}) {
  return (
    <label style={labelStyle}>
      <span style={labelTextStyle}>{label}</span>
      <input
        type="number"
        value={value ?? ""}
        min={min}
        step={step}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        style={inputStyle}
      />
    </label>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <label style={labelStyle}>
      <span style={labelTextStyle}>{label}</span>
      <input
        type="date"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        style={inputStyle}
      />
    </label>
  );
}

function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (b: boolean) => void;
}) {
  return (
    <label
      style={{
        ...labelStyle,
        flexDirection: "row",
        alignItems: "center",
        gap: "var(--space-8)",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span style={{ ...labelTextStyle, fontWeight: 500 }}>{label}</span>
    </label>
  );
}

function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  help,
}: {
  label: string;
  value: T | null;
  options: readonly T[];
  onChange: (v: T | null) => void;
  help?: string;
}) {
  return (
    <label style={labelStyle} title={help}>
      <span style={labelTextStyle}>{label}</span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange((e.target.value || null) as T | null)}
        style={inputStyle}
      >
        <option value="">(Unknown — needs configuration)</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function TriStateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
}) {
  return (
    <label style={labelStyle}>
      <span style={labelTextStyle}>{label}</span>
      <select
        value={value === null ? "" : value ? "true" : "false"}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "" ? null : v === "true");
        }}
        style={inputStyle}
      >
        <option value="">(Unknown)</option>
        <option value="true">yes</option>
        <option value="false">no</option>
      </select>
    </label>
  );
}

function ChipMultiSelect<T extends string>({
  label,
  values,
  options,
  onToggle,
}: {
  label: string;
  values: readonly T[];
  options: readonly T[];
  onToggle: (opt: T) => void;
}) {
  const set = new Set(values);
  return (
    <fieldset style={{ ...fieldsetStyle, margin: 0, padding: "var(--space-4) var(--space-8)" }}>
      <legend style={legendStyle}>{label}</legend>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-4)" }}>
        {options.map((o) => {
          const active = set.has(o);
          return (
            <label
              key={o}
              style={{
                ...chipButtonStyle,
                background: active ? "var(--status-canonical-ok-soft)" : "var(--color-surface-sunken)",
                borderColor: active ? "var(--status-canonical-ok)" : "var(--color-border-strong)",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={active}
                onChange={() => onToggle(o)}
                style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
                aria-label={o}
              />
              {o}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
